/**
 * AI Interview Modal
 *
 * Full-screen video interview experience for Virtual Assistants.
 * - Live camera preview via browser MediaRecorder API
 * - 5 personalized rounds generated from VA profile by Gemini AI
 * - Video upload to Supabase Storage
 * - Final completion screen
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Video, VideoOff, Mic, MicOff, Camera, Square,
  ChevronRight, Loader2, CheckCircle, AlertCircle,
  RefreshCw, Clock, Info, MessageSquare, Send, Type, Link
} from 'lucide-react';
import aiInterviewService from '../../services/aiInterviewService';
import { VideoRecorder, isBrowserSupported } from '../../utils/videoRecorder';
import { useAuth } from '../../hooks/useAuth';

// ─── Constants ────────────────────────────────────────────────────────────────

const RECORDING_COUNTDOWN = 3;   // seconds before recording starts
const MAX_ANSWER_SECS     = 180; // 3 minutes per answer
const TOTAL_ROUNDS        = parseInt(import.meta.env.VITE_AI_INTERVIEW_ROUNDS) || 5;

const STAGE = {
  PERMISSION:   'permission',    // Waiting for camera/mic permission
  INTRO:        'intro',         // Welcome screen before starting
  COUNTDOWN:    'countdown',     // 3-second countdown before recording
  RECORDING:    'recording',     // Currently recording
  TEXT_ANSWER:  'text_answer',   // Text-based answer input (fallback when no camera)
  SUBMITTING:   'submitting',    // Processing response
  RESULTS:      'results',       // Final interview results
  ERROR:        'error',         // Unrecoverable error
  IN_PROGRESS:  'in_progress',   // An interview is already in progress
};

function formatDuration(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ─── Main Component ───────────────────────────────────────────────────────────

const AIInterviewModal = ({
  isOpen,
  onClose,
  vaProfile,        // Full VA profile object
  jobContext = null, // { id, title, required_skills } for employer-requested interviews
  employerId = null,
  onInterviewComplete = null,
}) => {
  const { user } = useAuth();

  // ── State ──
  const [stage,            setStage]            = useState(STAGE.INTRO);
  const [sessionId,        setSessionId]        = useState(null);
  const [currentQuestion,  setCurrentQuestion]  = useState('');
  const [focusArea,        setFocusArea]        = useState('');
  const [round,            setRound]            = useState(1);
  const [totalRounds,      setTotalRounds]      = useState(TOTAL_ROUNDS);
  const [progress,         setProgress]         = useState(0);
  const [conversationLog,  setConversationLog]  = useState([]);
  const [countdown,        setCountdown]        = useState(RECORDING_COUNTDOWN);
  const [recordingSecs,    setRecordingSecs]    = useState(0);
  const [isVideoOn,        setIsVideoOn]        = useState(true);
  const [isMicOn,          setIsMicOn]          = useState(true);
  const [errorMsg,         setErrorMsg]         = useState('');
  const [results,          setResults]          = useState(null);
  const [uploadingVideo,   setUploadingVideo]   = useState(false);
  const [browserOk,        setBrowserOk]        = useState(true);
  const [textMode,         setTextMode]         = useState(false);
  const [textAnswer,       setTextAnswer]       = useState('');
  const [inProgressSessionId,  setInProgressSessionId]  = useState(null);
  const [errorType,             setErrorType]             = useState('camera'); // 'camera' | 'api'
  const [showCancelConfirm,     setShowCancelConfirm]     = useState(false);

  const [savedToUrl,        setSavedToUrl]        = useState(null);  // final video URL

  // ── Refs ──
  const videoPreviewRef    = useRef(null);
  const recorderRef        = useRef(null);
  const countdownTimerRef  = useRef(null);
  const recordingTimerRef  = useRef(null);
  const maxTimerRef        = useRef(null);
  const streamRef          = useRef(null);
  const sessionIdRef       = useRef(null);
  const roundRef           = useRef(1);

  // ── Helpers (defined before effects so refs are not in TDZ) ──
  const cleanupMedia = useCallback(() => {
    clearInterval(countdownTimerRef.current);
    clearInterval(recordingTimerRef.current);
    clearTimeout(maxTimerRef.current);

    if (recorderRef.current) {
      try { recorderRef.current.releaseStream(); } catch { /* stream already released */ }
      recorderRef.current = null;
    }

    streamRef.current = null;
  }, []);

  const resetState = useCallback(() => {
    setSessionId(null);
    sessionIdRef.current = null;
    setCurrentQuestion('');
    setFocusArea('');
    setRound(1);
    roundRef.current = 1;
    setTotalRounds(TOTAL_ROUNDS);
    setProgress(0);
    setConversationLog([]);
    setCountdown(RECORDING_COUNTDOWN);
    setRecordingSecs(0);
    setErrorMsg('');
    setResults(null);
    setUploadingVideo(false);
    setTextMode(false);
    setTextAnswer('');
    setInProgressSessionId(null);
    setErrorType('camera');
    setSavedToUrl(null);
    setShowCancelConfirm(false);
  }, []);

  // ── Effects: Init / Cleanup ──
  useEffect(() => {
    if (isOpen) {
      setBrowserOk(isBrowserSupported());
      setStage(STAGE.INTRO);
      resetState();
    } else {
      cleanupMedia();
    }
    return () => cleanupMedia();
  }, [isOpen, cleanupMedia, resetState]);

  // ── Escape key to close/cancel ──
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (stage === STAGE.RECORDING || stage === STAGE.COUNTDOWN) {
        setShowCancelConfirm(true);
      } else if (stage !== STAGE.SUBMITTING) {
        handleClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, stage]);

  // ── Setup Video Preview ──
  const attachStreamToPreview = useCallback(() => {
    if (videoPreviewRef.current && streamRef.current) {
      videoPreviewRef.current.srcObject = streamRef.current;
      videoPreviewRef.current.play().catch(() => {});
    }
  }, []);

  // Re-attach stream whenever the video element mounts (stage changes to COUNTDOWN/RECORDING)
  useEffect(() => {
    if ((stage === STAGE.COUNTDOWN || stage === STAGE.RECORDING) && streamRef.current) {
      attachStreamToPreview();
    }
  }, [stage, attachStreamToPreview]);

  // ── Permission & Camera ──
  const handleRequestPermissions = async () => {
    setStage(STAGE.PERMISSION);
    setErrorMsg('');

    try {
      const recorder = new VideoRecorder({
        uploadHandler: null,
        onChunkUploaded: () => {},
        onError: (err) => setErrorMsg(err.message),
        onDurationUpdate: (secs) => setRecordingSecs(secs),
      });

      const stream = await recorder.requestPermissions();
      recorderRef.current = recorder;
      streamRef.current = stream;

      // Start interview session
      await beginInterview();
    } catch (err) {
      console.error('[AIInterviewModal] Camera/mic permission error:', err.message);
      setErrorMsg(err.message);
      setStage(STAGE.ERROR);
    }
  };

  // ── Text-mode fallback (no camera needed) ──
  const handleStartTextMode = async () => {
    setTextMode(true);
    setStage(STAGE.PERMISSION);
    setErrorMsg('');
    await beginInterview(true);
  };

  const handleSubmitTextAnswer = async () => {
    const answer = textAnswer.trim();
    if (!answer) return;
    setStage(STAGE.SUBMITTING);
    setTextAnswer('');
    await submitAnswer(answer, null);
  };

  const beginInterview = async (isTextMode = false) => {
    try {
      const resp = await aiInterviewService.startInterview(
        user.id,
        vaProfile,
        jobContext,
        employerId
      );

      setSessionId(resp.sessionId);
      sessionIdRef.current = resp.sessionId;
      setCurrentQuestion(resp.question);
      setFocusArea(resp.focusArea || '');
      setRound(resp.round);
      roundRef.current = resp.round;
      setTotalRounds(resp.totalRounds);
      setProgress(resp.progress);

      setConversationLog([{
        type:    'ai',
        content: resp.question,
        round:   resp.round,
      }]);

      if (isTextMode) {
        setStage(STAGE.TEXT_ANSWER);
      } else {
        setStage(STAGE.COUNTDOWN);
        startCountdown();
      }
    } catch (err) {
      if (err.inProgressSessionId) {
        setInProgressSessionId(err.inProgressSessionId);
        setErrorMsg(err.message);
        setStage(STAGE.IN_PROGRESS);
      } else {
        setErrorMsg(err.message || 'Failed to start the interview. Please try again.');
        setErrorType(err.isServiceError ? 'api' : 'camera');
        setStage(STAGE.ERROR);
      }
    }
  };

  // ── Cancel in-progress interview & retry ──
  const handleCancelAndRestart = async (useTextMode = false) => {
    try {
      setStage(STAGE.SUBMITTING);
      await aiInterviewService.deleteInterview(inProgressSessionId);
      setInProgressSessionId(null);
      if (useTextMode) {
        await handleStartTextMode();
      } else {
        await handleRequestPermissions();
      }
    } catch (cancelErr) {
      console.error('[AIInterviewModal] Failed to cancel in-progress interview:', cancelErr);
      setErrorMsg('Failed to cancel the previous interview. Please try again.');
      setStage(STAGE.ERROR);
    }
  };

  // ── Countdown → Auto-start recording ──
  const startCountdown = () => {
    clearInterval(countdownTimerRef.current); // guard against duplicate intervals
    setCountdown(RECORDING_COUNTDOWN);
    let c = RECORDING_COUNTDOWN;

    countdownTimerRef.current = setInterval(() => {
      c -= 1;
      setCountdown(c);
      if (c <= 0) {
        clearInterval(countdownTimerRef.current);
        startRecording();
      }
    }, 1000);
  };

  // ── Recording ──
  const startRecording = async () => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId) {
      setErrorMsg('Interview session could not be started. Please try again.');
      setErrorType('api');
      setStage(STAGE.ERROR);
      return;
    }

    setStage(STAGE.RECORDING);
    setRecordingSecs(0);

    try {
      await recorderRef.current.start(currentSessionId, user.id, roundRef.current, vaProfile?.full_name);

      // Timer
      let secs = 0;
      recordingTimerRef.current = setInterval(() => {
        secs += 1;
        setRecordingSecs(secs);
      }, 1000);

      // Auto-stop at max
      maxTimerRef.current = setTimeout(() => {
        handleStopRecording();
      }, MAX_ANSWER_SECS * 1000);
    } catch (err) {
      setErrorMsg(err.message || 'Recording failed to start.');
      setStage(STAGE.ERROR);
    }
  };

  const handleStopRecording = async () => {
    clearInterval(recordingTimerRef.current);
    clearTimeout(maxTimerRef.current);
    setStage(STAGE.SUBMITTING);

    let videoUrl     = null;
    let storagePath  = null;
    let durationSecs = recordingSecs;

    try {
      setUploadingVideo(true);
      if (!recorderRef.current) throw new Error('Recorder not initialised');
      const result = await recorderRef.current.stop();
      videoUrl    = result.videoUrl;
      storagePath = result.storagePath;
      durationSecs = result.duration;
      setSavedToUrl(videoUrl ?? null);
    } catch (uploadErr) {
      console.warn('[AIInterviewModal] Video upload failed (continuing without):', uploadErr.message);
    } finally {
      setUploadingVideo(false);
    }

    // Attach video to session in background
    const activeSessionId = sessionIdRef.current;
    if (videoUrl && activeSessionId) {
      aiInterviewService.attachVideo(activeSessionId, videoUrl, storagePath, durationSecs)
        .catch((e) => console.warn('[AIInterviewModal] attachVideo error:', e));
    }

    // Build a transcript stand-in (answer from recording duration context)
    const placeholderAnswer = `[Video response — ${formatDuration(durationSecs)} recorded]`;

    await submitAnswer(placeholderAnswer, videoUrl, storagePath);
  };

  const submitAnswer = async (answer, videoChunkUrl = null, videoStoragePath = null) => {
    try {
      const resp = await aiInterviewService.submitResponse(
        sessionIdRef.current,  // use ref to avoid stale closure
        answer,
        vaProfile,
        videoChunkUrl,
        videoStoragePath
      );

      // Append user entry
      const userEntry = {
        type:       'user',
        content:    answer,
        round,
        videoUrl:   videoChunkUrl,
      };

      if (resp.done) {
        setConversationLog(prev => [...prev, userEntry]);
        setResults(resp);
        setStage(STAGE.RESULTS);
        onInterviewComplete?.(resp);
      } else {
        setCurrentQuestion(resp.question);

        setFocusArea(resp.focusArea || '');
        setRound(resp.round);
        roundRef.current = resp.round;
        setProgress(resp.progress);

        setConversationLog(prev => [
          ...prev,
          userEntry,
          { type: 'ai', content: resp.question, round: resp.round },
        ]);

        if (textMode) {
          setStage(STAGE.TEXT_ANSWER);
        } else {
          setStage(STAGE.COUNTDOWN);
          setRecordingSecs(0);
          startCountdown();
        }
      }
    } catch (err) {
      setErrorMsg(err.message || 'Failed to submit response. Please try again.');
      setStage(STAGE.ERROR);
    }
  };

  // ── Toggle tracks ──
  const toggleVideo = () => {
    if (!streamRef.current) return;
    streamRef.current.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsVideoOn(prev => !prev);
  };

  const toggleMic = () => {
    if (!streamRef.current) return;
    streamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setIsMicOn(prev => !prev);
  };

  // ── Cancel Interview ──
  const handleCancelInterview = () => {
    setShowCancelConfirm(true);
  };

  const confirmCancelInterview = async () => {
    setShowCancelConfirm(false);
    try {
      if (stage === STAGE.RECORDING || stage === STAGE.COUNTDOWN) {
        clearInterval(recordingTimerRef.current);
        clearInterval(countdownTimerRef.current);
        clearTimeout(maxTimerRef.current);
        if (recorderRef.current) {
          try { await recorderRef.current.stop(); } catch { /* ignore */ }
        }
      }

      const activeSessionId = sessionIdRef.current;
      if (activeSessionId) {
        try {
          await aiInterviewService.deleteInterview(activeSessionId);
        } catch (e) {
          console.error('[AIInterviewModal] Error cancelling session:', e);
        }
      }

      handleClose();
    } catch (err) {
      console.error('[AIInterviewModal] Error during cancel:', err);
      handleClose();
    }
  };

  // ── Close ──
  const handleClose = () => {
    cleanupMedia();
    onClose();
  };

  // ── Render guards ──
  if (!isOpen) return null;

  // ── Render ──
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-interview-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85"
    >
      {/* ── Cancel Confirmation Banner ── */}
      {showCancelConfirm && (
        <div className="absolute z-10 inset-0 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4 text-center">
            <h4 className="font-semibold text-gray-900 mb-2">Cancel Interview?</h4>
            <p className="text-sm text-gray-500 mb-5">Your progress will not be saved.</p>
            <div className="flex gap-3">
              <button
                autoFocus
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
              >
                Keep Going
              </button>
              <button
                onClick={confirmCancelInterview}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              {textMode ? <MessageSquare className="w-4 h-4 text-emerald-600" /> : <Video className="w-4 h-4 text-emerald-600" />}
            </div>
            <div>
              <h2 className="font-semibold text-gray-900 text-sm">
                AI Interview {textMode ? '(Text Mode)' : ''}
              </h2>
              {stage !== STAGE.INTRO && stage !== STAGE.RESULTS && stage !== STAGE.ERROR && (
                <p className="text-xs text-gray-500">
                  Round {round} of {totalRounds}
                  {focusArea ? ` — ${focusArea}` : ''}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Progress bar */}
            {stage !== STAGE.INTRO && stage !== STAGE.RESULTS && stage !== STAGE.ERROR && (
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-32 bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">{Math.round(progress * 100)}%</span>
              </div>
            )}
            <button
              onClick={handleClose}
              className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              aria-label="Close interview"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto">

          {/* ─── INTRO ─── */}
          {stage === STAGE.INTRO && (
            <div className="flex flex-col items-center justify-center p-8 gap-6 min-h-[400px]">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <Camera className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="text-center max-w-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  AI Video Interview
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Answer {TOTAL_ROUNDS} personalized questions based on your profile.
                  Each response is recorded.
                  The interview takes approximately <strong>10–15 minutes</strong>.
                </p>
              </div>

              {!browserOk && (
                <div className="w-full max-w-sm bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Your browser may have limited video recording support.
                  For the best experience, use Chrome or Edge.
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-4 w-full max-w-sm space-y-2">
                {[
                  ['Round 1', 'Introduction & Background'],
                  ['Round 2', 'Technical Skills'],
                  ['Round 3', 'Scenario-Based'],
                  ['Round 4', 'Behavioral'],
                  ['Round 5', 'Motivation & Value'],
                ].map(([r, desc]) => (
                  <div key={r} className="flex items-center gap-3 text-sm">
                    <span className="text-xs font-semibold text-emerald-600 w-16">{r}</span>
                    <span className="text-gray-600">{desc}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-center gap-3">
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Maybe Later
                  </button>
                  <button
                    onClick={handleRequestPermissions}
                    className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-2"
                  >
                    <Video className="w-4 h-4" />
                    Start with Video
                  </button>
                </div>
                <button
                  onClick={handleStartTextMode}
                  className="text-sm text-emerald-600 hover:text-emerald-700 underline underline-offset-2 flex items-center gap-1.5 transition-colors"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  No camera? Continue with text-only
                </button>
              </div>

              <p className="text-xs text-gray-400 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Camera &amp; microphone needed for video mode
              </p>
            </div>
          )}

          {/* ─── PERMISSION LOADING ─── */}
          {stage === STAGE.PERMISSION && (
            <div className="flex flex-col items-center justify-center p-8 gap-4 min-h-[400px]">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              <p className="text-gray-600 text-sm">Requesting camera &amp; microphone access…</p>
              <p className="text-gray-400 text-xs">Please allow access when prompted by your browser.</p>
            </div>
          )}

          {/* ─── COUNTDOWN ─── */}
          {stage === STAGE.COUNTDOWN && (
            <div className="grid md:grid-cols-2 gap-0 min-h-[500px]">
              {/* Video side */}
              <div className="relative bg-gray-900 flex items-center justify-center">
                <video
                  ref={videoPreviewRef}
                  muted
                  playsInline
                  autoPlay
                  aria-label="Camera preview"
                  className={`w-full max-h-[400px] h-full object-cover ${!isVideoOn ? 'opacity-0' : ''}`}
                />
                {!isVideoOn && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <VideoOff className="w-12 h-12 text-gray-500" />
                  </div>
                )}
                <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                  Preview
                </div>
                {/* Countdown overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <div className="text-center">
                    <div className="text-7xl font-bold text-white mb-2">{countdown}</div>
                    <p className="text-white/80 text-sm">Recording starts in…</p>
                  </div>
                </div>
              </div>

              {/* Question side */}
              <div className="p-6 flex flex-col justify-center gap-4">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full self-start">
                  Round {round} of {totalRounds}
                </div>
                <h3 className="text-base font-semibold text-gray-800 leading-relaxed">
                  {currentQuestion}
                </h3>
                <p className="text-xs text-gray-400">
                  You have up to {MAX_ANSWER_SECS / 60} minutes to answer.
                  Recording will start automatically.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={toggleVideo}
                    className={`p-2 rounded-lg border text-sm flex items-center gap-1.5 ${isVideoOn ? 'border-gray-200 text-gray-600' : 'border-red-200 text-red-600 bg-red-50'}`}
                  >
                    {isVideoOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={toggleMic}
                    className={`p-2 rounded-lg border text-sm flex items-center gap-1.5 ${isMicOn ? 'border-gray-200 text-gray-600' : 'border-red-200 text-red-600 bg-red-50'}`}
                  >
                    {isMicOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  onClick={handleCancelInterview}
                  className="w-full mt-2 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium text-sm transition-colors"
                >
                  Cancel Interview
                </button>
              </div>
            </div>
          )}

          {/* ─── RECORDING ─── */}
          {stage === STAGE.RECORDING && (
            <div className="grid md:grid-cols-2 gap-0 min-h-[500px]">
              {/* Video side */}
              <div className="relative bg-gray-900 flex items-center justify-center">
                <video
                  ref={videoPreviewRef}
                  muted
                  playsInline
                  autoPlay
                  aria-label="Camera preview"
                  className={`w-full max-h-[400px] h-full object-cover ${!isVideoOn ? 'opacity-0' : ''}`}
                />
                {!isVideoOn && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <VideoOff className="w-12 h-12 text-gray-500" />
                  </div>
                )}

                {/* Recording indicator */}
                <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600/90 text-white text-xs px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  REC {formatDuration(recordingSecs)}
                </div>

                {/* Time warning */}
                {recordingSecs >= MAX_ANSWER_SECS - 30 && (
                  <div className="absolute bottom-3 left-3 right-3 bg-amber-500/90 text-white text-xs px-3 py-1.5 rounded-lg text-center">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {MAX_ANSWER_SECS - recordingSecs}s remaining
                  </div>
                )}
              </div>

              {/* Question + controls */}
              <div className="p-6 flex flex-col justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full mb-3">
                    Round {round} of {totalRounds}
                  </div>
                  <h3 className="text-base font-semibold text-gray-800 leading-relaxed mb-4">
                    {currentQuestion}
                  </h3>

                </div>

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <button
                      onClick={toggleVideo}
                      className={`p-2 rounded-lg border text-xs flex items-center gap-1 ${isVideoOn ? 'border-gray-200 text-gray-600' : 'border-red-200 text-red-600 bg-red-50'}`}
                    >
                      {isVideoOn ? <Video className="w-3.5 h-3.5" /> : <VideoOff className="w-3.5 h-3.5" />}
                      <span>{isVideoOn ? 'On' : 'Off'}</span>
                    </button>
                    <button
                      onClick={toggleMic}
                      className={`p-2 rounded-lg border text-xs flex items-center gap-1 ${isMicOn ? 'border-gray-200 text-gray-600' : 'border-red-200 text-red-600 bg-red-50'}`}
                    >
                      {isMicOn ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
                      <span>{isMicOn ? 'On' : 'Off'}</span>
                    </button>
                  </div>

                  <button
                    onClick={handleStopRecording}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm transition-colors"
                  >
                    <Square className="w-4 h-4" />
                    Stop &amp; Submit Answer
                  </button>

                  <button
                    onClick={handleCancelInterview}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium text-sm transition-colors"
                  >
                    Cancel Interview
                  </button>

                  <p className="text-xs text-center text-gray-400">
                    Max {MAX_ANSWER_SECS / 60} min · Answer stops automatically
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ─── SUBMITTING ─── */}
          {stage === STAGE.SUBMITTING && (
            <div className="flex flex-col items-center justify-center p-8 gap-4 min-h-[400px]">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          {uploadingVideo
            ? (
                <div className="w-full max-w-xs">
                  <p className="text-gray-700 font-medium mb-2">Uploading your response…</p>
                </div>
              )
            : <p className="text-gray-700 font-medium">Processing your answer…</p>
          }
              <p className="text-gray-400 text-xs">This usually takes a few seconds.</p>
            </div>
          )}

          {/* ─── TEXT ANSWER (fallback mode) ─── */}
          {stage === STAGE.TEXT_ANSWER && (
            <div className="grid md:grid-cols-2 gap-0 min-h-[500px]">
              {/* Info side */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 flex flex-col justify-center gap-4">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-white/70 px-3 py-1 rounded-full self-start">
                  Round {round} of {totalRounds} — Text Mode
                </div>
                <h3 className="text-base font-semibold text-gray-800 leading-relaxed">
                  {currentQuestion}
                </h3>
              </div>

              {/* Text input side */}
              <div className="p-6 flex flex-col justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Type className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Your Answer</span>
                  </div>
                  <textarea
                    value={textAnswer}
                    onChange={(e) => setTextAnswer(e.target.value)}
                    placeholder="Type your answer here… Be detailed and professional, as if you were speaking in a real interview."
                    className="w-full h-48 border border-gray-200 rounded-xl p-4 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder:text-gray-400"
                    maxLength={3000}
                    autoFocus
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-400">
                      {textAnswer.length}/3000 characters
                    </p>
                    <p className="text-xs text-gray-400">
                      Tip: Aim for 50–200 words for a thorough answer
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleSubmitTextAnswer}
                  disabled={!textAnswer.trim()}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Submit Answer
                </button>
              </div>
            </div>
          )}

          {/* ─── ERROR ─── */}
          {stage === STAGE.ERROR && (
            <div className="flex flex-col items-center justify-center p-8 gap-4 min-h-[400px]">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-500" />
              </div>
              <div className="text-center max-w-sm">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {errorType === 'api' ? 'AI Service Unavailable' : 'Camera / Microphone Issue'}
                </h3>
                <p className="text-gray-500 text-sm mb-3">{errorMsg}</p>
                {errorType === 'api' ? (
                  <div className="text-left bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
                    <p className="font-semibold text-gray-600 mb-1">What you can do:</p>
                    <p>• Wait a moment and try again — the AI service may be temporarily busy</p>
                    <p>• Check your internet connection</p>
                    <p>• If the problem persists, try again later or contact support</p>
                  </div>
                ) : (
                  <div className="text-left bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
                    <p className="font-semibold text-gray-600 mb-1">Troubleshooting:</p>
                    <p>• Check that a camera and microphone are connected</p>
                    <p>• Click the camera/lock icon in the address bar and allow access</p>
                    <p>• On Windows: Settings → Privacy → Camera/Microphone must be enabled</p>
                    <p>• Close other apps that may be using the camera</p>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
                {errorType === 'api' ? (
                  <button
                    onClick={() => { resetState(); setStage(STAGE.INTRO); }}
                    className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => { resetState(); setStage(STAGE.INTRO); }}
                      className="flex-1 px-4 py-2 rounded-lg border border-emerald-200 text-sm text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry Camera
                    </button>
                    <button
                      onClick={handleStartTextMode}
                      className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center justify-center gap-2"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Text Mode
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ─── IN PROGRESS ─── */}
          {stage === STAGE.IN_PROGRESS && (
            <div className="flex flex-col items-center justify-center p-8 gap-4 min-h-[400px]">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-amber-500" />
              </div>
              <div className="text-center max-w-sm">
                <h3 className="font-semibold text-gray-900 mb-2">Interview Already in Progress</h3>
                <p className="text-gray-500 text-sm mb-3">
                  You have an incomplete interview session. You can cancel it and start fresh, or close this dialog and come back later.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => handleCancelAndRestart(false)}
                  className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center justify-center gap-2"
                >
                  <Video className="w-4 h-4" />
                  Cancel &amp; Start Video
                </button>
                <button
                  onClick={() => handleCancelAndRestart(true)}
                  className="flex-1 px-4 py-2 rounded-lg border border-emerald-200 text-sm text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Cancel &amp; Start Text
                </button>
              </div>
            </div>
          )}

          {stage === STAGE.RESULTS && results && (
            <div className="flex flex-col items-center justify-center p-8 gap-6 min-h-[400px]">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="text-center max-w-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Interview Complete!
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  You answered all {totalRounds} questions. Your interview has been recorded
                  and is now available for employers to review.
                </p>
              </div>
              {savedToUrl && (
                <a
                  href={savedToUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
                >
                  <Link className="w-3.5 h-3.5" />
                  View recording
                </a>
              )}
            </div>
          )}
        </div>

        {/* ── Footer buttons for results ── */}
        {stage === STAGE.RESULTS && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center shrink-0">
            <button
              onClick={() => { resetState(); setStage(STAGE.INTRO); cleanupMedia(); }}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-100 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Retake Interview
            </button>
            <button
              onClick={handleClose}
              className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 flex items-center gap-2"
            >
              Done <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AIInterviewModal;
